/// <amd-module name="TFS/NetPromoterSurvey/Recommenders/AnonymousRecommender" />
import { IInteraction } from "TFS/NetPromoterSurvey/client/Contracts/Interaction";
import { DataKeys } from "TFS/NetPromoterSurvey/Common";
import { DataConstants, IDecision, IRecommender } from "TFS/NetPromoterSurvey/Recommenders/Recommender";
import { format } from "VSS/Core/Util/String";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { traceError } from "VSS/Platform/Trace";

export interface AnonymousRecommenderSettings {
    samplingRate: number;
    delayInDaysForSubmit: number;
    delayInDaysForPostpone: number;
    maxAllowableDeferTimes: number;
    minDelayInHoursBetweenPrompts: number;
}

/**
 * Make recommendation for net promoter survey for anonymous users. Fetches minimal registry values for sampling rate
 */
export class AnonymousRecommender implements IRecommender {
    private static readonly LocalStorageRecommendationKey = "NetPromoterSurvey_Recommendation";
    private static readonly LocalStorageInteractionKey = "NetPromoterSurvey_Interaction";

    private _context: IVssPageContext;
    public settings: AnonymousRecommenderSettings;

    constructor(context: IVssPageContext) {
        this._context = context;
    }

    public recommmend(): Promise<IDecision> {
        return new Promise<IDecision>(resolve => {
            this._getNPSData(this._context).then((setting: AnonymousRecommenderSettings) => {
                this.settings = setting;
                const localStorageRecommendation = localStorage.getItem(AnonymousRecommender.LocalStorageRecommendationKey);
                if (localStorageRecommendation == null) {
                    this.logRecommendation();
                }

                const lastRecommendationDate: Date = new Date(Number(localStorageRecommendation));

                const interactionDateReviver = (key: any, value: any) => {
                    if (key === "timestamp") {
                        return new Date(value);
                    }
                    return value;
                };
                const lastInteraction: IInteraction = JSON.parse(
                    localStorage.getItem(AnonymousRecommender.LocalStorageInteractionKey) || "{}",
                    interactionDateReviver
                );

                // create a negative recommendation
                let recommendation: { [key: string]: string };

                // set recommendation to positive if user should be prompted
                this.shouldPrompt(lastRecommendationDate, lastInteraction).then((decision: boolean) => {
                    if (decision) {
                        recommendation = !!lastInteraction && !!lastInteraction.interactionInputData ? lastInteraction.interactionInputData : {};
                        recommendation[DataKeys.AnonymousAccess] = "true";
                        recommendation[DataConstants.EngagementId] = "NetPromoterSurvey";
                        recommendation[DataKeys.MaxAllowableDeferTimes] = this.settings.maxAllowableDeferTimes.toString();
                    }

                    resolve({
                        isPositive: decision,
                        data: recommendation
                    });
                });
            });
        });
    }

    public logRecommendation(): Promise<void> {
        return new Promise<void>(resolve => {
            localStorage.setItem(AnonymousRecommender.LocalStorageRecommendationKey, new Date().getTime().toString());
            resolve();
        });
    }

    public logInteraction(data: { [key: string]: string }): Promise<void> {
        return new Promise<void>(() => {
            const interaction: IInteraction = {
                id: "NetPromoterSurvey",
                accountId: "00000000-0000-0000-0000-000000000000",
                projectId: "00000000-0000-0000-0000-000000000000",
                timestamp: new Date(),
                interactionInputData: data
            };
            localStorage.setItem(AnonymousRecommender.LocalStorageInteractionKey, JSON.stringify(interaction));
        });
    }

    public shouldPrompt(lastRecommendationDate: Date, lastInteraction: IInteraction): Promise<boolean> {
        return new Promise<boolean>(resolve => {
            // if not web storage available, don't prompt
            if (!this.evaluateLastRecommendationDate(lastRecommendationDate)) {
                resolve(false);
            }

            if (!this.evaluateNextPromptDate(lastInteraction)) {
                resolve(false);
            }

            if (!this.isUserInRandomSample()) {
                resolve(false);
            }

            resolve(true);
        });
    }

    /**
     * Compare the last recommendation date + minimum delay in hours between two prompts versus UTC now.
     * @param lastRecommendation
     * @param minDelayInHoursBetweenPrompts
     * @returns true if UTC now >= last recommendation date + minimum hours; otherwise, false.
     */
    public evaluateLastRecommendationDate(lastRecommendation: Date): boolean {
        if (lastRecommendation.getTime() !== new Date(Number(null)).getTime()) {
            const minDelayInHoursBetweenPrompts: number = this.settings.minDelayInHoursBetweenPrompts;
            if (lastRecommendation != null) {
                return (new Date().getTime() - lastRecommendation.getTime()) / (1000 * 3600) > minDelayInHoursBetweenPrompts;
            }
        }

        // delay first prompt to give users time to engage with VSTS
        // if Web Storage is not available for current user, will never prompt
        return false;
    }

    /**
     * Compare the last recommendation date + minimum delay in hours between two prompts versus UTC now.
     * @param interaction
     * @param settings
     * @returns true if UTC now >= last recommendation date + minimum days; otherwise, false.
     */
    public evaluateNextPromptDate(interaction: IInteraction): boolean {
        if (!!interaction && !!interaction.interactionInputData && !!interaction.interactionInputData[DataKeys.InteractionType]) {
            const nextPromptDate: Date = this.getNextPromptDate(interaction.timestamp, interaction.interactionInputData[DataKeys.InteractionType]);
            if (new Date().getTime() < nextPromptDate.getTime()) {
                return false;
            }
        }
        return true;
    }

    /**
     * Returns true if in sample and false otherwise.
     * @param settings
     * @returns
     */
    public isUserInRandomSample(): boolean {
        const samplingRate: number = this.settings.samplingRate;
        const random: number = this._random(new Date().getTime());
        return random < samplingRate;
    }

    public getNextPromptDate(interactionDate: Date, interactionType: string): Date {
        const nextDate: Date = new Date(interactionDate.getTime());
        let delayInDays: number = this.settings.delayInDaysForSubmit;
        if (interactionType.toLowerCase() === "postpone") {
            delayInDays = this.settings.delayInDaysForPostpone;
        }
        nextDate.setDate(interactionDate.getDate() + delayInDays);
        return nextDate;
    }

    private _random(seed: number): number {
        const x: number = Math.sin(seed++) * 10000;
        return x - Math.floor(x);
    }

    /*
    Fetches from the data provider 
    */
    private _getNPSData = (pageContext: IVssPageContext): Promise<AnonymousRecommenderSettings> => {
        const contributionService = pageContext.getService<IVssContributionService>("IVssContributionService");
        const PROVIDER_ID = "ms.vss-tfs-web.net-promoter-survey-context";
        return contributionService
            .getDataAsync(PROVIDER_ID)
            .then((data: AnonymousRecommenderSettings) => {
                if (data) {
                    const formattedData = {
                        samplingRate: data.samplingRate,
                        delayInDaysForSubmit: data.delayInDaysForSubmit,
                        delayInDaysForPostpone: data.delayInDaysForPostpone,
                        maxAllowableDeferTimes: data.maxAllowableDeferTimes,
                        minDelayInHoursBetweenPrompts: data.minDelayInHoursBetweenPrompts
                    };
                    return Promise.resolve(formattedData);
                }
                return Promise.reject(undefined);
            })
            .catch((reason: any) => {
                traceError(this._context, "Engagement", "NetPromoterSurvey", format("Data provider {0} failed: {1}", PROVIDER_ID, reason));
                return Promise.reject(undefined);
            });
    };
}
